import { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router";

import { ApiClientProvider } from "@/api/ApiClientProvider";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { setRecentProject } from "@/helpers/recent-project";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { getLocalStorageValue } from "@/hooks/useLocalStorage";
import { routes } from "@/routes";

import type { ProjectId } from "./ProjectContext";
import { ProjectContext } from "./ProjectContext";
import { resolveProject } from "./resolveProject";
import { StartupLoading } from "./StartupLoading";

/** Resolves a route or nested modal project and supplies its immutable API scope. Only root selection is persisted. */
export function ProjectLoader({
  projectId,
  children,
}: {
  projectId?: string;
  children: React.ReactNode;
}): React.ReactNode {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const { data: projects, isFetching, error } = useLinkedProjects();
  const [recentProjectId, setRecentProjectId] = useState<string | undefined>(() =>
    getLocalStorageValue(LOCAL_STORAGE_KEYS.RECENT_PROJECT, undefined),
  );
  const project = projects ? resolveProject(projects, { projectId, slug, recentProjectId }) : undefined;
  const selectedId = project?.id;

  // Remember only the selection, not a copy of project data. A portfolio route has no slug;
  // it should retain the root selection when leaving a project route without resetting children.
  if (!projectId && selectedId && recentProjectId !== selectedId) {
    setRecentProjectId(selectedId);
  }
  useEffect(() => {
    if (!projectId && selectedId) {
      setRecentProject(selectedId);
    }
  }, [projectId, selectedId]);

  if (!projects) {
    if (error) {
      return <ErrorPage error={error} />;
    }

    return projectId ? <FullSizeLoader withPadding /> : <StartupLoading />;
  }
  if (projects.length === 0 && isFetching) {
    return <StartupLoading />;
  }
  if (projects.length === 0 && !projectId) {
    const brand = new URLSearchParams(location.search).get("brand");
    const search = brand ? `?${new URLSearchParams({ brand })}` : "";

    return <Navigate to={routes.registration.main() + search} replace />;
  }
  if (!project) {
    return <ErrorPage status={404} />;
  }

  return (
    <ProjectContext
      value={{ projectId: project.id as ProjectId, projectSlug: project.slug, projectAppBranding: project.appBranding }}
    >
      <ApiClientProvider projectId={project.id} appBranding={project.appBranding}>
        {children}
      </ApiClientProvider>
    </ProjectContext>
  );
}
